import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, ArrowRightLeft, Filter, X } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  useTemplates,
  useBookings,
  useReassignBooking,
  type BookingRow,
} from '@/lib/booking';
import { useBrief } from '@/lib/brief';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/coordinate')({
  staticData: { crumb: 'Koordynacja' },
  component: CoordinatePage,
});

const SLOT_MIN = 30;
const DAY_START_MIN = 8 * 60; // 08:00
const DAY_END_MIN = 20 * 60; // 20:00
const SLOTS_PER_DAY = (DAY_END_MIN - DAY_START_MIN) / SLOT_MIN;

interface Doctor {
  id: string;
  full_name: string;
}

function CoordinatePage() {
  const { data: brief } = useBrief();
  const templates = useTemplates();
  const bookings = useBookings();
  const reassign = useReassignBooking();

  const allDoctors = ((brief?.team ?? []) as Doctor[]) || [];
  const services = React.useMemo(() => {
    const set = new Set<string>();
    for (const t of templates.data ?? []) {
      if (t.service_slugs) for (const s of t.service_slugs) set.add(s);
    }
    return Array.from(set).sort();
  }, [templates.data]);

  const [date, setDate] = React.useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [serviceFilter, setServiceFilter] = React.useState('');
  const [selectedBooking, setSelectedBooking] = React.useState<BookingRow | null>(null);
  const [feedback, setFeedback] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );

  const doctors = serviceFilter
    ? allDoctors.filter((d) =>
        (templates.data ?? [])
          .filter((t) => t.doctor_id === d.id)
          .some((t) => !t.service_slugs || t.service_slugs.includes(serviceFilter))
      )
    : allDoctors;

  // Build per-cell data: bookings keyed by `${doctor_id}:${slot_idx}`.
  const dayStartEpoch = Math.floor(date.getTime() / 1000) + DAY_START_MIN * 60;
  const dayEndEpoch = Math.floor(date.getTime() / 1000) + DAY_END_MIN * 60;
  const weekday = ((date.getDay() + 6) % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6;

  const cellBookings = React.useMemo(() => {
    const map = new Map<string, BookingRow>();
    for (const b of bookings.data ?? []) {
      if (b.start_at < dayStartEpoch || b.start_at >= dayEndEpoch) continue;
      if (b.state === 'cancelled') continue;
      const slotIdx = Math.floor((b.start_at - dayStartEpoch) / (SLOT_MIN * 60));
      if (slotIdx < 0 || slotIdx >= SLOTS_PER_DAY) continue;
      map.set(`${b.doctor_id}:${slotIdx}`, b);
    }
    return map;
  }, [bookings.data, dayStartEpoch, dayEndEpoch]);

  // For each (doctor, slot), is the doctor available?
  const cellAvailable = React.useMemo(() => {
    const map = new Map<string, boolean>();
    for (const t of templates.data ?? []) {
      if (t.weekday !== weekday) continue;
      if (serviceFilter && t.service_slugs && !t.service_slugs.includes(serviceFilter)) continue;
      const tStart = parseHm(t.start_time);
      const tEnd = parseHm(t.end_time);
      for (let m = DAY_START_MIN; m < DAY_END_MIN; m += SLOT_MIN) {
        if (m >= tStart && m + SLOT_MIN <= tEnd) {
          map.set(`${t.doctor_id}:${(m - DAY_START_MIN) / SLOT_MIN}`, true);
        }
      }
    }
    return map;
  }, [templates.data, weekday, serviceFilter]);

  const shiftDay = (delta: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d);
  };

  const onReassign = async (toDoctorId: string, reason?: string) => {
    if (!selectedBooking) return;
    setFeedback(null);
    try {
      await reassign.mutateAsync({
        id: selectedBooking.id,
        new_doctor_id: toDoctorId,
        reason,
      });
      setSelectedBooking(null);
      setFeedback({ kind: 'ok', text: 'Wizyta przeniesiona.' });
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
            Koordynacja lekarzy
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Wszyscy lekarze obok siebie. Kliknij wizytę, aby ją przenieść do innego lekarza
            (kolizje czasowe są blokowane).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => shiftDay(-1)} aria-label="Poprzedni dzień">
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <span className="font-display text-sm font-semibold text-text">
            {date.toLocaleDateString('pl-PL', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <Button variant="ghost" size="icon" onClick={() => shiftDay(1)} aria-label="Następny dzień">
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const d = new Date();
              d.setHours(0, 0, 0, 0);
              setDate(d);
            }}
          >
            Dziś
          </Button>
        </div>
      </header>

      {feedback ? (
        <p
          role="status"
          className={cn(
            'rounded-md px-4 py-2 text-sm ring-1',
            feedback.kind === 'ok'
              ? 'bg-success/10 text-success ring-success/20'
              : 'bg-error/10 text-error ring-error/20'
          )}
        >
          {feedback.text}
        </p>
      ) : null}

      {services.length > 0 ? (
        <div className="flex items-center gap-2">
          <Filter className="size-4 text-text-muted" aria-hidden="true" />
          <span className="text-xs text-text-muted">Usługa:</span>
          <Select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="h-9 max-w-xs"
          >
            <option value="">— wszystkie —</option>
            {services.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          {serviceFilter ? (
            <Button variant="ghost" size="sm" onClick={() => setServiceFilter('')}>
              Wyczyść
            </Button>
          ) : null}
        </div>
      ) : null}

      {doctors.length === 0 ? (
        <Card>
          <CardContent>
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak lekarzy. Dodaj zespół w Treści strony → Zespół.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md ring-1 ring-black/5">
          <div className="overflow-x-auto">
            <div
              className="grid min-w-full"
              style={{ gridTemplateColumns: `64px repeat(${doctors.length}, minmax(110px, 1fr))` }}
            >
              <div className="sticky left-0 z-10 bg-surface px-2 py-2 text-xs font-semibold text-text-subtle ring-1 ring-black/5">
                godz.
              </div>
              {doctors.map((d) => (
                <div
                  key={d.id}
                  className="bg-surface px-2 py-2 text-center text-xs font-semibold text-text ring-1 ring-black/5"
                >
                  {d.full_name || d.id}
                </div>
              ))}
              {Array.from({ length: SLOTS_PER_DAY }).map((_, slotIdx) => {
                const min = DAY_START_MIN + slotIdx * SLOT_MIN;
                const hh = String(Math.floor(min / 60)).padStart(2, '0');
                const mm = String(min % 60).padStart(2, '0');
                return (
                  <React.Fragment key={slotIdx}>
                    <div className="sticky left-0 z-10 bg-bg px-2 py-1.5 text-[10px] font-mono text-text-subtle ring-1 ring-black/5">
                      {hh}:{mm}
                    </div>
                    {doctors.map((d) => {
                      const key = `${d.id}:${slotIdx}`;
                      const b = cellBookings.get(key);
                      const available = cellAvailable.get(key);
                      return (
                        <Cell
                          key={key}
                          booking={b}
                          available={available ?? false}
                          onClick={() => b && setSelectedBooking(b)}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {selectedBooking ? (
        <ReassignDrawer
          booking={selectedBooking}
          doctors={allDoctors}
          onClose={() => setSelectedBooking(null)}
          onReassign={onReassign}
          busy={reassign.isPending}
        />
      ) : null}
    </div>
  );
}

function Cell({
  booking,
  available,
  onClick,
}: {
  booking: BookingRow | undefined;
  available: boolean;
  onClick: () => void;
}) {
  if (booking) {
    const stateColor =
      booking.state === 'completed'
        ? 'bg-success/15 ring-success/30 text-success'
        : booking.state === 'no_show'
        ? 'bg-error/10 ring-error/20 text-error'
        : 'bg-accent/10 ring-accent/30 text-accent';
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'cursor-pointer truncate px-1.5 py-1 text-left text-[11px] leading-tight ring-1 ring-inset',
          stateColor,
          'hover:brightness-95'
        )}
        title={`${booking.patient_name} · ${booking.service_slug ?? '—'}`}
      >
        <div className="truncate font-semibold">{booking.patient_name}</div>
        {booking.service_slug ? (
          <div className="truncate text-[9px] opacity-75">{booking.service_slug}</div>
        ) : null}
      </button>
    );
  }
  if (available) {
    return <div className="bg-bg ring-1 ring-inset ring-black/5" />;
  }
  return (
    <div className="bg-surface/40 ring-1 ring-inset ring-black/5 [background-image:repeating-linear-gradient(45deg,transparent_0,transparent_3px,rgba(0,0,0,0.04)_3px,rgba(0,0,0,0.04)_4px)]" />
  );
}

function ReassignDrawer({
  booking,
  doctors,
  onClose,
  onReassign,
  busy,
}: {
  booking: BookingRow;
  doctors: Doctor[];
  onClose: () => void;
  onReassign: (toDoctorId: string, reason?: string) => void;
  busy: boolean;
}) {
  const [target, setTarget] = React.useState('');
  const [reason, setReason] = React.useState('');

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-end bg-text/40 p-0"
    >
      <div className="h-full w-full max-w-md space-y-4 overflow-y-auto bg-bg p-6 shadow-xl ring-1 ring-black/10">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-text">Wizyta</h2>
            <p className="mt-1 text-xs text-text-muted">
              {new Date(booking.start_at * 1000).toLocaleString('pl-PL')} · stan:{' '}
              <span className="font-mono">{booking.state}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="rounded-md p-1 text-text-muted hover:text-text"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-2 rounded-md bg-surface p-4 ring-1 ring-black/5">
          <p className="text-sm">
            <strong className="font-semibold">{booking.patient_name}</strong>
          </p>
          <p className="text-xs text-text-muted">
            tel.{' '}
            <a href={`tel:${booking.patient_phone}`} className="text-accent hover:underline">
              {booking.patient_phone}
            </a>
          </p>
          {booking.service_slug ? (
            <p className="text-xs text-text-muted">
              usługa: <span className="font-mono">{booking.service_slug}</span>
            </p>
          ) : null}
          <p className="text-xs text-text-muted">
            obecnie u: <span className="font-mono">{booking.doctor_id}</span>
          </p>
          {booking.notes ? (
            <p className="rounded-md bg-bg p-2 text-xs text-text">{booking.notes}</p>
          ) : null}
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-subtle">
            Przenieś do innego lekarza
          </p>
          <Select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full"
          >
            <option value="">— wybierz —</option>
            {doctors
              .filter((d) => d.id !== booking.doctor_id)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.full_name || d.id}
                </option>
              ))}
          </Select>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Powód (opcjonalnie)"
            className="mt-2 h-9 w-full rounded-md bg-bg px-3 text-sm ring-1 ring-border focus:outline-2 focus:-outline-offset-2 focus:outline-accent"
          />
          <Button
            type="button"
            className="mt-3 w-full"
            disabled={!target || busy}
            onClick={() => onReassign(target, reason || undefined)}
          >
            <ArrowRightLeft className="size-4" aria-hidden="true" />
            Przenieś wizytę
          </Button>
        </div>
      </div>
    </div>
  );
}

function parseHm(s: string): number {
  const [h, m] = s.split(':').map((x) => parseInt(x, 10));
  return (h ?? 0) * 60 + (m ?? 0);
}
